import './ops-detalhe-modal.js';
import type { Ops, EtapaOps } from '@pgcustom/shared-types';
import { ETAPA_CONCLUSAO } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { PgKanban, PgKanbanColumn, PgKanbanCard, PgChart, PgKpiCard } from '@pgcustom/design-system';
import type { OpsApi } from './ops.api.js';
import type { PermissoesOps } from './ops.permissoes.js';
import { ETAPAS_ROTULO, rotuloPrioridade, toneDaPrioridade } from './etapa-ops.js';
import type { ErpOpsDetalheModal } from './ops-detalhe-modal.js';

const COLUNAS: PgKanbanColumn[] = ETAPAS_ROTULO.map((opcao) => ({ key: opcao.valor, label: opcao.rotulo }));

/**
 * `<erp-producao-page>` — quadro Kanban de Produção (Sprint 6,
 * `ROADMAP.md`). Mesmo padrão de `<erp-pedidos-page>`/
 * `<erp-financeiro-page>`: estado carregando/vazio/erro/sem-permissão
 * via `pg-empty-state`, dado real via `OpsApi`. Arrastar um cartão para
 * outra coluna chama `POST /ops/:id/etapa`; a etapa só muda de verdade
 * (visualmente) depois da resposta da API confirmar — nunca otimista,
 * para nunca mostrar um estado que o backend rejeitou (ex: mover para
 * "Enviado" sem técnico atribuído).
 */
export class ErpProducaoPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _opsApi: OpsApi | null = null;
  private _permissoes: PermissoesOps = { ler: false, atualizar: false };
  private _usuarioAtualId: string | null = null;

  private resultado: Resultado<Ops[]> = { status: 'carregando' };
  private mensagemErroMovimento = '';

  private modalDetalhe!: ErpOpsDetalheModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set opsApi(valor: OpsApi) {
    this._opsApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesOps) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  set usuarioAtualId(valor: string | null) {
    this._usuarioAtualId = valor;
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._opsApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0;
        }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin: 2px 0 var(--pg-space-5, 24px); }
        .erro-movimento {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-erro, #e5484d);
          margin-bottom: var(--pg-space-4, 16px);
        }
        .erro-movimento:empty { display: none; }
        .layout { display: grid; grid-template-columns: minmax(0, 220px) minmax(0, 1fr); gap: var(--pg-space-4, 16px); align-items: stretch; margin-bottom: var(--pg-space-5, 24px); }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
      </style>
      <h1>Produção</h1>
      <div class="subtitulo">Quadro operacional de ordens de produção — arraste um cartão para mudar de etapa</div>

      <div class="layout">
        <pg-kpi-card id="kpi-ativas" label="OPS ativas" icone="producao" estado="carregando"></pg-kpi-card>
        <pg-card padding="compacto">
          <span slot="header">Distribuição por etapa</span>
          <div id="funil"><pg-empty-state variant="carregando" titulo="Distribuição por etapa"></pg-empty-state></div>
        </pg-card>
      </div>

      <div class="erro-movimento" id="erro-movimento"></div>
      <div id="conteudo"></div>
      <erp-ops-detalhe-modal id="modal-detalhe"></erp-ops-detalhe-modal>
    `;

    this.modalDetalhe = this.root.querySelector('#modal-detalhe') as ErpOpsDetalheModal;
    if (this._opsApi) this.modalDetalhe.opsApi = this._opsApi;
    this.modalDetalhe.permissoes = this._permissoes;
    this.modalDetalhe.usuarioAtualId = this._usuarioAtualId;
    this.modalDetalhe.aoAtualizar = () => void this.carregar();

    this.atualizarConteudo();
  }

  private async carregar(): Promise<void> {
    const api = this._opsApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.atualizarConteudo();
      return;
    }

    this.resultado = { status: 'carregando' };
    this.atualizarConteudo();

    this.resultado = await api.listar({ limit: 100 });
    this.atualizarConteudo();
  }

  private async moverCartao(cardId: string, paraColuna: EtapaOps): Promise<void> {
    if (!this._opsApi || !this._permissoes.atualizar) return;

    this.mensagemErroMovimento = '';
    try {
      await this._opsApi.alterarEtapa(cardId, paraColuna);
      await this.carregar();
    } catch (erro) {
      this.mensagemErroMovimento = erro instanceof ApiError ? erro.message : 'não foi possível mover o cartão.';
      const alvo = this.root.querySelector('#erro-movimento');
      if (alvo) alvo.textContent = this.mensagemErroMovimento;
    }
  }

  private atualizarConteudo(): void {
    const conteudo = this.root.querySelector('#conteudo');
    if (!conteudo) return;

    const erroMovimentoEl = this.root.querySelector('#erro-movimento');
    if (erroMovimentoEl) erroMovimentoEl.textContent = this.mensagemErroMovimento;

    if (this.resultado.status === 'ok') {
      conteudo.innerHTML = '<pg-kanban></pg-kanban>';
      const kanban = conteudo.querySelector('pg-kanban') as PgKanban;
      kanban.columns = COLUNAS;
      kanban.cards = this.resultado.dado.map(opsParaCartao);

      kanban.addEventListener('pg-card-move', (evento) => {
        const { cardId, paraColuna } = (evento as CustomEvent<{ cardId: string; paraColuna: string }>).detail;
        void this.moverCartao(cardId, paraColuna as EtapaOps);
      });
      kanban.addEventListener('pg-card-click', (evento) => {
        const { cardId } = (evento as CustomEvent<{ cardId: string }>).detail;
        this.modalDetalhe.abrirParaVer(cardId);
      });

      this.atualizarResumo(this.resultado.dado);
    } else {
      const { variant, titulo, descricao } = estadoVazio(this.resultado);
      conteudo.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;

      const kpi = this.root.querySelector('#kpi-ativas') as PgKpiCard | null;
      if (kpi) {
        if (this.resultado.status === 'vazio') {
          kpi.setAttribute('estado', 'ok');
          kpi.setAttribute('valor', '0');
        } else {
          kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
        }
      }
      const funil = this.root.querySelector('#funil');
      if (funil) funil.innerHTML = `<pg-empty-state variant="${variant}" titulo="Distribuição por etapa" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    }
  }

  /** KPI/funil calculados client-side sobre a MESMA lista já buscada para o quadro (`api.listar({ limit: 100 })`) — mesma técnica de "página capada" já usada no Dashboard para papéis sem `relatorios:ler`, nunca um agregado inventado. */
  private atualizarResumo(ops: Ops[]): void {
    const contagem: Record<EtapaOps, number> = { recebido: 0, pintura: 0, montagem: 0, testes: 0, embalagem: 0, enviado: 0 };
    for (const o of ops) contagem[o.etapa]++;
    const ativas = ETAPAS_ROTULO.filter((opcao) => opcao.valor !== ETAPA_CONCLUSAO).reduce((soma, opcao) => soma + contagem[opcao.valor], 0);

    const kpi = this.root.querySelector('#kpi-ativas') as PgKpiCard | null;
    if (kpi) {
      kpi.setAttribute('estado', 'ok');
      kpi.setAttribute('valor', String(ativas));
      if (ops.length === 100) kpi.setAttribute('dica', '100+ (primeira página)');
    }

    const funil = this.root.querySelector('#funil');
    if (funil) {
      funil.innerHTML = '<pg-chart></pg-chart>';
      const chart = funil.querySelector('pg-chart') as PgChart;
      chart.type = 'bar';
      chart.series = [{ key: 'quantidade', label: 'OPS', color: 'var(--pg-color-destaque)' }];
      chart.points = ETAPAS_ROTULO.map((opcao) => ({ label: opcao.rotulo, values: { quantidade: contagem[opcao.valor] } }));
      chart.formatarValor = (valor) => String(valor);
    }
  }
}

function opsParaCartao(ops: Ops): PgKanbanCard {
  return {
    id: ops.id,
    columnKey: ops.etapa,
    titulo: `Pedido #${ops.pedidoId.slice(0, 8)}`,
    subtitulo: ops.tecnicoId ? `Técnico #${ops.tecnicoId.slice(0, 8)}` : 'sem técnico',
    badgeTexto: rotuloPrioridade(ops.prioridade),
    badgeTone: toneDaPrioridade(ops.prioridade),
  };
}

function estadoVazio(resultado: Resultado<Ops[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Produção', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Produção', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Produção', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhuma OPS encontrada', descricao: 'OPS nascem automaticamente quando um pedido é pago' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-producao-page', ErpProducaoPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-producao-page': ErpProducaoPage;
  }
}
